/**
 * AP-35 Nachtrag 12 — **Anordnung der Verkaufszeilen**: verschieben, einrücken,
 * ausrücken. Reine Rechnung ohne React und ohne Server.
 *
 * Das Ergebnis ist immer dasselbe: die **ganze** Liste in Anzeigereihenfolge, je
 * Zeile mit ihrem Elternteil. Genau das nimmt `POST …/sales-lines/reorder`
 * entgegen — Ziehen und Einrücken sind dieselbe Bewegung, und eine Teilangabe
 * ließe offen, was mit dem Rest geschieht.
 *
 * Es gibt **genau eine Ebene** (Blatt 7 2.1): Eine Unterposition bekommt keine
 * eigenen Kinder. Eine Hauptzeile mit Kindern lässt sich nicht einrücken — sonst
 * entstünde die zweite.
 */

/** Was die Anordnung von einer Zeile wissen muss. */
export interface AnordnungsZeile {
  id: string
  children: { id: string }[]
}

export interface Anordnung {
  id: string
  parentId: string | null
}

/** Die ganze Liste flach, in Anzeigereihenfolge. */
export function alsAnordnung(zeilen: readonly AnordnungsZeile[]): Anordnung[] {
  return zeilen.flatMap((z) => [{ id: z.id, parentId: null }, ...z.children.map((k) => ({ id: k.id, parentId: z.id }))])
}

/** Elternteil einer Zeile — `undefined`, wenn es sie nicht gibt. */
function elternVon(zeilen: readonly AnordnungsZeile[], id: string): AnordnungsZeile | null | undefined {
  if (zeilen.some((z) => z.id === id)) return null
  const eltern = zeilen.find((z) => z.children.some((k) => k.id === id))
  return eltern ?? undefined
}

/** Die Reihe, in der eine Zeile steht: die Hauptzeilen oder die Kinder ihres Elternteils. */
function reiheVon(zeilen: readonly AnordnungsZeile[], id: string): string[] | null {
  const eltern = elternVon(zeilen, id)
  if (eltern === undefined) return null
  return eltern === null ? zeilen.map((z) => z.id) : eltern.children.map((k) => k.id)
}

/** `reihe` ohne `bewegt`, dafür mit `bewegt` vor `vor` (oder am Ende). */
function neueReihe(reihe: readonly string[], bewegt: readonly string[], vor: string | null): string[] {
  const rest = reihe.filter((id) => !bewegt.includes(id))
  const i = vor === null ? rest.length : rest.indexOf(vor)
  const stelle = i < 0 ? rest.length : i
  return [...rest.slice(0, stelle), ...bewegt, ...rest.slice(stelle)]
}

/** Dieselbe Reihenfolge? Dann gibt es nichts zu senden. */
function unveraendert(a: readonly Anordnung[], b: readonly Anordnung[]): boolean {
  return a.length === b.length && a.every((x, i) => x.id === b[i].id && x.parentId === b[i].parentId)
}

/**
 * Die gegriffene Zeile (und die markierten **ihrer Ebene**) vor `vorId` ablegen.
 * `vorId === null` heißt ans Ende der Ebene. Eine Hauptzeile nimmt ihre Kinder
 * mit; über Ebenen hinweg wird nicht abgelegt — dafür gibt es Ein- und Ausrücken.
 *
 * `null`, wenn sich nichts ändert oder das Ziel nicht zur Ebene gehört.
 */
export function verschiebeVerkauf(
  zeilen: readonly AnordnungsZeile[],
  gezogen: string,
  markiert: readonly string[],
  vorId: string | null,
): Anordnung[] | null {
  const reihe = reiheVon(zeilen, gezogen)
  if (!reihe) return null
  if (vorId !== null && !reihe.includes(vorId)) return null
  const bewegt = reihe.filter((id) => id === gezogen || markiert.includes(id))
  if (vorId !== null && bewegt.includes(vorId)) return null
  const geordnet = neueReihe(reihe, bewegt, vorId)

  const eltern = elternVon(zeilen, gezogen)
  const vorher = alsAnordnung(zeilen)
  const nachher =
    eltern === null
      ? geordnet.flatMap((id) => {
          const z = zeilen.find((x) => x.id === id)!
          return [{ id, parentId: null }, ...z.children.map((k) => ({ id: k.id, parentId: id }))]
        })
      : zeilen.flatMap((z) => [
          { id: z.id, parentId: null },
          ...(z.id === eltern!.id ? geordnet : z.children.map((k) => k.id)).map((id) => ({ id, parentId: z.id })),
        ])
  return unveraendert(vorher, nachher) ? null : nachher
}

/**
 * Einrücken: Die Hauptzeile wird **letzte Unterposition der Zeile darüber**. Geht
 * nicht für die erste Zeile, nicht für eine Unterposition und nicht für eine Zeile,
 * die selbst Kinder hat (das wäre die zweite Ebene).
 */
export function einrueckenVerkauf(zeilen: readonly AnordnungsZeile[], id: string): Anordnung[] | null {
  const i = zeilen.findIndex((z) => z.id === id)
  if (i <= 0 || zeilen[i].children.length) return null
  const darueber = zeilen[i - 1]
  return zeilen.flatMap((z) =>
    z.id === id
      ? []
      : [{ id: z.id, parentId: null }, ...z.children.map((k) => ({ id: k.id, parentId: z.id })), ...(z.id === darueber.id ? [{ id, parentId: z.id }] : [])],
  )
}

/** Ausrücken: Die Unterposition wird Hauptzeile **direkt hinter ihrem bisherigen Elternteil**. */
export function ausrueckenVerkauf(zeilen: readonly AnordnungsZeile[], id: string): Anordnung[] | null {
  const eltern = zeilen.find((z) => z.children.some((k) => k.id === id))
  if (!eltern) return null
  return zeilen.flatMap((z) => [
    { id: z.id, parentId: null },
    ...z.children.filter((k) => k.id !== id).map((k) => ({ id: k.id, parentId: z.id })),
    ...(z.id === eltern.id ? [{ id, parentId: null }] : []),
  ])
}
